import React, { useState, useCallback } from "react";
import Header from "../Profile/Header";
import ReportingForm from "./ReportingForm";
import { MessageSquare, Phone, Mail, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { useContact } from "../context/ContactContext";

const Report = () => {
  const {
    contactData,
    loading: contactLoading,
    error: contactError,
  } = useContact();

  // Form validation state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    issueType: '',
    priority: 'medium',
    description: '',
    attachments: []
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  // Validation rules
  const validationRules = {
    name: {
      required: true,
      minLength: 2,
      maxLength: 50,
      pattern: /^[a-zA-Z\s]+$/,
      message: 'Name must be 2-50 characters and contain only letters and spaces'
    },
    email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address'
    },
    phone: {
      required: false,
      pattern: /^[\+]?[1-9][\d]{0,15}$/,
      message: 'Please enter a valid phone number'
    },
    subject: {
      required: true,
      minLength: 5,
      maxLength: 100,
      message: 'Subject must be 5-100 characters long'
    },
    issueType: {
      required: true,
      message: 'Please select an issue type'
    },
    description: {
      required: true,
      minLength: 10,
      maxLength: 1000,
      message: 'Description must be 10-1000 characters long'
    }
  };

  // Validation function
  const validateField = useCallback((fieldName, value) => {
    const rule = validationRules[fieldName];
    if (!rule) return null;

    // Required field validation
    if (rule.required && (!value || value.toString().trim() === '')) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }

    // Skip other validations if field is empty and not required
    if (!rule.required && (!value || value.toString().trim() === '')) {
      return null;
    }

    const stringValue = value.toString().trim();

    // Length validations
    if (rule.minLength && stringValue.length < rule.minLength) {
      return rule.message || `Minimum length is ${rule.minLength} characters`;
    }

    if (rule.maxLength && stringValue.length > rule.maxLength) {
      return rule.message || `Maximum length is ${rule.maxLength} characters`;
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(stringValue)) {
      return rule.message || 'Invalid format';
    }

    return null;
  }, []);

  // Validate all fields
  const validateForm = useCallback(() => {
    const errors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  }, [formData, validateField]);

  // Handle field change with real-time validation
  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear submit status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }

    // Real-time validation for the changed field
    const error = validateField(fieldName, value);
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, [validateField, submitStatus]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate API call - replace with actual submission logic
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock success/failure for demonstration
      const isSuccess = Math.random() > 0.2; // 80% success rate

      if (isSuccess) {
        setSubmitStatus('success');
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          issueType: '',
          priority: 'medium',
          description: '',
          attachments: []
        });
        setFormErrors({});
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, formData]);

  // Handle file attachments
  const handleFileChange = useCallback((files) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];

    const validFiles = Array.from(files).filter(file => {
      if (file.size > maxSize) {
        setFormErrors(prev => ({
          ...prev,
          attachments: `File ${file.name} is too large. Maximum size is 5MB.`
        }));
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        setFormErrors(prev => ({
          ...prev,
          attachments: `File ${file.name} has unsupported format. Allowed: JPEG, PNG, GIF, PDF, TXT.`
        }));
        return false;
      }

      return true;
    });

    if (validFiles.length === files.length) {
      setFormErrors(prev => ({ ...prev, attachments: null }));
    }

    handleFieldChange('attachments', validFiles);
  }, [handleFieldChange]);

  return (
    <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 h-auto pb-8 w-full">
      {/* Header */}
      <Header activeSection="report" />

      {/* Main Report Content */}
      <main className="relative mx-auto w-[90%] lg:w-[80%] xl:w-[75%] 2xl:w-[70%] mt-6 md:mt-1 px-4 md:px-0">
        {/* Page Header */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center w-full text-teal-800 mb-6 md:mb-4  mx-auto">
          <div className="flex items-center gap-1">
          <MessageSquare className="w-8 h-8 text-teal-600" />                
          <h1
            className="text-2xl md:text-3xl lg:text-4xl tracking-wider text-center font-bold text-teal-800 px-3 md:px-2 py-2"
            // style={{
            //   WebkitTextStroke: "1px white",
            //   textStroke: "1px white",
            // }}
          >
            Report an Issue
          </h1>
          </div>
        </header>

        {/* Report Content */}
        <div className="space-y-8">
          {/* Reporting Section */}
          <section
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300"
            aria-labelledby="reporting-heading"
          >
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-orange-50">
              <h2
                id="reporting-heading"
                className="text-xl font-semibold text-gray-900 flex items-center gap-2"
              >
                {/* <MessageSquare className="w-5 h-5 text-teal-600" /> */}
                Report an Issue
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Help us improve by reporting bugs or providing feedback
              </p>
            </div>

            {/* Form Validation Status */}
            {submitStatus && (
              <div className={`p-4 border-b ${
                submitStatus === 'success'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {submitStatus === 'success' ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-800 font-medium">
                        Report submitted successfully! We'll get back to you soon.
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="text-red-800 font-medium">
                        Please check the form for errors and try again.
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced ReportingForm with validation props */}
            <ReportingForm
              formData={formData}
              formErrors={formErrors}
              isSubmitting={isSubmitting}
              onFieldChange={handleFieldChange}
              onSubmit={handleSubmit}
              onFileChange={handleFileChange}
              validationRules={validationRules}
            />
          </section>

          {/* Contact Information Section */}
          <section
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300"
            aria-labelledby="contact-heading"
          >
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-teal-50">
              <h2
                id="contact-heading"
                className="text-xl font-semibold text-gray-900 flex items-center gap-2"
              >
                <Phone className="w-5 h-5 text-orange-600" />
                Need Direct Contact?
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Reach out to us directly for urgent issues or immediate
                assistance
              </p>
            </div>

            <div className="p-6">
              {contactLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                    <span className="text-gray-600">
                      Loading contact information...
                    </span>
                  </div>
                </div>
              ) : contactError && !contactData ? (
                <div className="text-center py-8">
                  <p className="text-red-600 mb-2">
                    Unable to load contact information
                  </p>
                  <p className="text-sm text-gray-500">
                    Please try again later or use the form above
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone Contact */}
                  {contactData?.phone && (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg border border-teal-200 hover:shadow-md transition-all duration-200">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                          <Phone
                            className="w-6 h-6 text-white"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Call Us
                        </h3>
                        <a
                          href={`tel:${contactData.phone.replace(/\s+/g, "")}`}
                          className="text-teal-700 hover:text-teal-800 font-medium text-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded"
                          aria-label={`Call us at ${contactData.phone}`}
                        >
                          {contactData.phone}
                        </a>
                        {contactData?.hours && (
                          <p className="text-sm text-gray-600 mt-1">
                            {contactData.hours}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Email Contact */}
                  {contactData?.email && (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200 hover:shadow-md transition-all duration-200">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                          <Mail
                            className="w-6 h-6 text-white"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Email Us
                        </h3>
                        <a
                          href={`mailto:${contactData.email}`}
                          className="text-orange-700 hover:text-orange-800 font-medium text-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded break-all"
                          aria-label={`Send email to ${contactData.email}`}
                        >
                          {contactData.email}
                        </a>
                        <p className="text-sm text-gray-600 mt-1">
                          We typically reply within 1–2 business days
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Form Validation Help Section */}
          {/* <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Form Guidelines
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <h4 className="font-medium mb-2">Required Fields:</h4>
                <ul className="space-y-1">
                  <li>• Name (2-50 characters, letters only)</li>
                  <li>• Email (valid email format)</li>
                  <li>• Subject (5-100 characters)</li>
                  <li>• Issue Type (select from dropdown)</li>
                  <li>• Description (10-1000 characters)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">File Attachments:</h4>
                <ul className="space-y-1">
                  <li>• Maximum size: 5MB per file</li>
                  <li>• Allowed formats: JPEG, PNG, GIF, PDF, TXT</li>
                  <li>• Optional but helpful for bug reports</li>
                </ul>
              </div>
            </div>
          </section> */}
        </div>
      </main>
    </div>
  );
};

export default Report;
